import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

export function HeroSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFinal, setShowFinal] = useState(false);
  const { t } = useLanguage();

  const rotatingLines = [
    t("hero.rotating.1"),
    t("hero.rotating.2"),
    t("hero.rotating.3"),
    t("hero.rotating.4"),
    t("hero.rotating.5"),
  ];

  useEffect(() => {
    if (showFinal) return;

    const interval = setInterval(() => {
      if (currentIndex < rotatingLines.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setShowFinal(true);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentIndex, showFinal, rotatingLines.length]);

  const scrollToGrowthSystem = () => {
    const element = document.getElementById("growth-system");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px]" />

        {/* Subtle rotating alignment loop */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[550px] sm:h-[550px] opacity-[0.10]"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 200 200" fill="none" className="w-full h-full">
            {/* Circular path */}
            <circle cx="100" cy="100" r="70" stroke="hsl(var(--primary))" strokeWidth="1.2" strokeDasharray="6 4" />

            {/* Node circles at 4 cardinal points */}
            <circle cx="100" cy="30" r="10" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="hsl(var(--primary)/0.15)" />
            <circle cx="170" cy="100" r="10" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="hsl(var(--primary)/0.15)" />
            <circle cx="100" cy="170" r="10" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="hsl(var(--primary)/0.15)" />
            <circle cx="30" cy="100" r="10" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="hsl(var(--primary)/0.15)" />

            {/* Small icons inside nodes — Target (top) */}
            <line x1="100" y1="26" x2="100" y2="34" stroke="hsl(var(--primary))" strokeWidth="1" />
            <line x1="96" y1="30" x2="104" y2="30" stroke="hsl(var(--primary))" strokeWidth="1" />
            <circle cx="100" cy="30" r="4" stroke="hsl(var(--primary))" strokeWidth="0.8" fill="none" />

            {/* Zap (right) */}
            <path d="M168 96 L172 100 L168 100 L172 104" stroke="hsl(var(--primary))" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none" />

            {/* TrendingUp (bottom) */}
            <path d="M96 173 L100 167 L103 171 L106 167" stroke="hsl(var(--primary))" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none" />

            {/* BarChart (left) */}
            <line x1="27" y1="104" x2="27" y2="98" stroke="hsl(var(--primary))" strokeWidth="1.2" />
            <line x1="30" y1="104" x2="30" y2="96" stroke="hsl(var(--primary))" strokeWidth="1.2" />
            <line x1="33" y1="104" x2="33" y2="99" stroke="hsl(var(--primary))" strokeWidth="1.2" />

            {/* Arrow curves between nodes (clockwise) */}
            <path d="M110 32 C 135 35, 160 60, 165 90" stroke="hsl(var(--primary))" strokeWidth="1" fill="none" />
            <polygon points="165,90 161,84 168,86" fill="hsl(var(--primary))" />

            <path d="M168 110 C 165 135, 135 160, 110 168" stroke="hsl(var(--primary))" strokeWidth="1" fill="none" />
            <polygon points="110,168 116,164 114,171" fill="hsl(var(--primary))" />

            <path d="M90 168 C 65 165, 40 140, 32 110" stroke="hsl(var(--primary))" strokeWidth="1" fill="none" />
            <polygon points="32,110 36,116 29,114" fill="hsl(var(--primary))" />

            <path d="M32 90 C 35 65, 60 40, 90 32" stroke="hsl(var(--primary))" strokeWidth="1" fill="none" />
            <polygon points="90,32 84,36 86,29" fill="hsl(var(--primary))" />
          </svg>
        </motion.div>
      </div>

      <div className="section-container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/50 mb-8"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">{t("hero.badge")}</span>
          </motion.div>

          {/* Main headline */}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-6">
            {t("hero.headline")}{" "}
            <span className="text-primary">{t("hero.headlineHighlight")}</span>
          </h1>

          {/* Rotating text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="h-16 sm:h-20 flex items-center justify-center mb-8"
          >
            <AnimatePresence mode="wait">
              {!showFinal ? (
                <motion.p
                  key={currentIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="text-xl sm:text-2xl lg:text-3xl text-muted-foreground"
                >
                  {rotatingLines[currentIndex]}
                </motion.p>
              ) : (
                <motion.p
                  key="final"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gradient"
                >
                  {t("hero.final")}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            {t("hero.subheadline")}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button variant="hero" size="xl" asChild>
              <Link to="/contact">
                {t("hero.cta.primary")}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button variant="heroOutline" size="xl" onClick={scrollToGrowthSystem}>
              {t("hero.cta.secondary")}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-primary"
          />
        </div>
      </motion.div>
    </section>
  );
}
